/**
 * Project ownership manifest — the ONE authoritative dependency strategy
 * for permanent project deletion (spec §11B).
 *
 * Three phases in a row (9B assembly components, 9C labor settlements,
 * 9D document extractions) shipped a new table that referenced accounting
 * rows and broke project deletion with a foreign-key error found only by
 * hand during acceptance. The fix is structural rather than another manual
 * patch:
 *
 *  1. every table in the schema is classified here — either PROJECT-OWNED
 *     (with the predicate that selects the project's rows and its position
 *     in the deletion order) or explicitly NOT project data, with a reason;
 *  2. `ProjectDeletionService` executes THIS plan and nothing else, so the
 *     order is deterministic and reviewable in one place;
 *  3. `tests/projectOwnership.test.ts` reads the real migrated schema and
 *     fails when a table is unclassified, when the order contradicts the
 *     actual foreign keys, or when rows survive a deletion.
 *
 * Adding a table therefore cannot silently miss deletion coverage: the
 * suite fails until the table is classified.
 *
 * This module is pure (strings only, no I/O) and portable: predicates use
 * plain subselects, never SQLite-specific syntax.
 */

/** A table whose rows belong to exactly one project. */
export interface ProjectOwnedTable {
  table: string;
  /**
   * WHERE predicate selecting the rows owned by a project. Every `?` is
   * bound to the project id, so the same predicate serves DELETE (removal)
   * and SELECT COUNT(*) (the residue check the tests use).
   */
  where: string;
  /** Number of `?` placeholders in `where` (all the project id). */
  params: number;
  /** Why this ordering/predicate is what it is, when non-obvious. */
  why?: string;
}

export type DeletionStep =
  | ({ kind: "delete" } & ProjectOwnedTable)
  /** A non-DELETE statement the plan needs (severing a self-reference). */
  | { kind: "sql"; sql: string; params: number; why: string }
  /**
   * A stage the service implements itself because ownership is conditional.
   * `tables` are the tables it may write, so coverage still accounts for
   * them and the FK-order check can place them.
   */
  | { kind: "special"; id: "doomed-purchases" | "orphaned-attachments"; tables: string[]; why: string };

const P = "?";
const DESIGNS_OF_PROJECT = `(SELECT id FROM designs WHERE project_id = ${P})`;
const FLOORS_OF_PROJECT = `(SELECT id FROM floors WHERE project_id = ${P})`;
const FEATURES_OF_PROJECT = `(SELECT id FROM project_features WHERE project_id = ${P})`;
const INSTANCES_OF_PROJECT = `(SELECT i.id FROM assembly_instances i
     JOIN project_features f ON f.id = i.project_feature_id WHERE f.project_id = ${P})`;
const COMPONENTS_OF_PROJECT = `(SELECT c.id FROM assembly_instance_components c
     JOIN assembly_instances i ON i.id = c.instance_id
     JOIN project_features f ON f.id = i.project_feature_id WHERE f.project_id = ${P})`;
/** Extractions filed under the project OR imported into one of its purchases. */
const EXTRACTIONS_OF_PROJECT = `(SELECT id FROM document_extractions WHERE project_id = ${P}
     OR imported_purchase_id IN (SELECT l.purchase_id FROM purchase_lines l
        JOIN purchase_allocations a ON a.line_id = l.id WHERE a.project_id = ${P}))`;

/**
 * THE DELETION ORDER. Children before parents; every entry's position is
 * verified against the schema's real foreign keys by the coverage test.
 */
export const PROJECT_DELETION_PLAN: DeletionStep[] = [
  {kind:"delete",table:"budget_grid_views70",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"worksheet_issue_attachments99",params:1,where:`issue_id IN (SELECT id FROM worksheet_issues99 WHERE project_id=${P})`},
  {kind:"delete",table:"worksheet_issues99",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"contractor_payment_links99",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"gc_compensation_portions",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"gc_compensation_associations",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"contractor_events99",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"contractor_agreements99",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"lender_draw_maps99",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"lender_worksheets99",params:1,where:`project_id=${P}`},

  {kind:"delete",table:"budget_approvals",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"spending_submissions",params:1,where:`project_id=${P}`},
  {kind:"delete",table:"receipt_import_claims",params:2,where:`extraction_id IN ${EXTRACTIONS_OF_PROJECT}`},
  {kind:"delete",table:"receipt_review_events",params:2,where:`extraction_id IN ${EXTRACTIONS_OF_PROJECT}`},
  {kind:"delete",table:"receipt_line_splits",params:3,where:`project_id=${P} OR line_id IN (SELECT id FROM extraction_lines WHERE extraction_id IN ${EXTRACTIONS_OF_PROJECT})`},
  {kind:"delete",table:"draw_request_funding",params:1,where:`request_id IN (SELECT id FROM draw_requests WHERE project_id=${P})`},
  {kind:"delete",table:"draw_request_decisions",params:1,where:`request_id IN (SELECT id FROM draw_requests WHERE project_id=${P})`},
  {kind:"delete",table:"draw_request_lines",params:1,where:`request_id IN (SELECT id FROM draw_requests WHERE project_id=${P})`},
  {kind:"delete",table:"draw_request_attachments",params:1,where:`request_id IN (SELECT id FROM draw_requests WHERE project_id=${P})`},
  {kind:"sql",sql:`UPDATE draw_requests SET amendment_of=NULL WHERE project_id=${P}`,params:1,why:"Request amendments retain their chain until explicit project deletion."},
  {kind:"delete",table:"draw_requests",params:1,where:`project_id=${P}`},
  // §93: nullable ownership retains business commitments/facilities. Only
  // the explicit project's rows are deleted; allocations never own debt.
  { kind: "delete", table: "commitment_matches", params: 1, where: `commitment_id IN (SELECT id FROM financial_commitments WHERE project_id=${P})` },
  { kind: "delete", table: "commitment_revisions", params: 1, where: `commitment_id IN (SELECT id FROM financial_commitments WHERE project_id=${P})` },
  { kind: "delete", table: "commitment_occurrence_overrides", params: 1, where: `commitment_id IN (SELECT id FROM financial_commitments WHERE project_id=${P})` },
  { kind: "delete", table: "financial_commitments", params: 1, where: `project_id=${P}` },
  { kind: "delete", table: "financial_cash_activities", params: 1, where: `project_id=${P}` },
  { kind: "delete", table: "financing_facility_allocations", params: 2, where: `project_id=${P} OR facility_id IN (SELECT id FROM financing_facilities WHERE project_id=${P})` },
  { kind: "delete", table: "financing_rate_changes", params: 1, where: `facility_id IN (SELECT id FROM financing_facilities WHERE project_id=${P})` },
  { kind: "delete", table: "loan_activities", params: 1, where: `facility_id IN (SELECT id FROM financing_facilities WHERE project_id=${P})` },
  // ---- Capital planning / financing (spec §73) ----------------------
  //
  // Draws and the agreement/extraction records reference facilities, so
  // the tree unwinds leaf-first. Payment plans, rental assumptions and
  // variance notes are plain project rows. Business-level planning
  // (capital_sources, scenario_projects, plan_settings, plan_overrides)
  // is deliberately NOT here — see NON_PROJECT_TABLES.
  {
    kind: "delete",
    table: "lender_draws",
    params: 1,
    where: `facility_id IN (SELECT id FROM financing_facilities WHERE project_id = ${P})`,
  },
  {
    kind: "delete",
    table: "financing_facility_attachments",
    params: 1,
    where: `facility_id IN (SELECT id FROM financing_facilities WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "financing_extractions", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "financing_facilities", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "payment_plans", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "rental_assumptions", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "variance_notes", params: 1, where: `project_id = ${P}` },
  // Spec §74: authorized category budgets + their variance justifications.
  { kind: "delete", table: "category_budgets", params: 1, where: `project_id = ${P}` },
  // Spec §75: the forecast's planning assumptions and phase-timing
  // experiments. Shifts reference project_tasks, so they go before them.
  { kind: "delete", table: "forecast_task_shifts", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "forecast_assumptions", params: 1, where: `project_id = ${P}` },

  {kind:"delete",table:"contractor_quote_revisions",params:1,where:`project_id = ${P}`},
  // ---- Contractor bid packages and quotes (spec §68) -----------------
  //
  // Quotes reference the package they answered, and their coverage and
  // attachments reference the quote, so the tree unwinds from the leaves.
  // This sits FIRST because contractor_quotes references bid_packages and
  // both reference designs, which are deleted much further down.
  {
    kind: "delete",
    table: "contractor_quote_attachments",
    params: 1,
    where: `quote_id IN (SELECT id FROM contractor_quotes WHERE project_id = ${P})`,
  },
  {
    kind: "delete",
    table: "design_annotations",
    params: 1,
    where: `design_id IN (SELECT id FROM designs WHERE project_id = ${P})`,
  },
  {
    // Spec §85: which doors a plan replaces in place. Decisions only —
    // they go with the design, before the openings they point at.
    kind: "delete",
    table: "opening_replacements",
    params: 1,
    where: `design_id IN (SELECT id FROM designs WHERE project_id = ${P})`,
  },
  {
    kind: "delete",
    table: "room_finish_overrides",
    params: 1,
    where: `project_id = ${P}`,
  },
  {
    kind: "delete",
    table: "room_existing_finishes",
    params: 1,
    where: `project_id = ${P}`,
  },
  {
    kind: "delete",
    table: "project_finish_defaults",
    params: 1,
    where: `project_id = ${P}`,
  },
  {
    kind: "delete",
    table: "contractor_quote_lines",
    params: 1,
    where: `quote_id IN (SELECT id FROM contractor_quotes WHERE project_id = ${P})`,
  },
  {
    kind: "delete",
    table: "contractor_quote_coverage",
    params: 1,
    where: `quote_id IN (SELECT id FROM contractor_quotes WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "contractor_quotes", params: 1, where: `project_id = ${P}` },
  {
    kind: "delete",
    table: "bid_package_scopes",
    params: 1,
    where: `package_id IN (SELECT id FROM bid_packages WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "bid_packages", params: 1, where: `project_id = ${P}` },
  // The decisions a person made about how to contract the work. They point
  // at designs and rooms, so they go before either.
  { kind: "delete", table: "bid_decisions", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "bid_trade_settings", params: 1, where: `project_id = ${P}` },
  // Spec §76B: presentation decisions — how each scope was shown.
  { kind: "delete", table: "bid_presentations", params: 1, where: `project_id = ${P}` },
  // Spec §78R: management work-item wording — presentation decisions.
  { kind: "delete", table: "budget_line_labels", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "bid_callouts", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "bid_manual_items", params: 1, where: `project_id = ${P}` },

  // ---- Document intelligence (Phase 9D) -----------------------------
  // Extraction candidates reference purchases, purchase lines, products,
  // features and proposed lines, so the whole tree goes first.
  {
    kind: "delete",
    table: "extraction_regions",
    params: 2,
    where: `extraction_id IN ${EXTRACTIONS_OF_PROJECT}`,
  },
  {
    kind: "delete",
    table: "extraction_lines",
    params: 2,
    where: `extraction_id IN ${EXTRACTIONS_OF_PROJECT}`,
  },
  {
    kind: "delete",
    table: "document_extractions",
    params: 2,
    where: `id IN ${EXTRACTIONS_OF_PROJECT}`,
  },

  // ---- Accounting (Phases 2, 5, 9C) ---------------------------------
  // Labor settlements point at the expense/allocation holding the money
  // and at the proposed lines they group, so they precede all three.
  {
    kind: "delete",
    table: "labor_settlement_lines",
    params: 1,
    where: `settlement_id IN (SELECT id FROM labor_settlements WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "labor_settlements", params: 1, where: `project_id = ${P}` },
  {
    kind: "delete",
    table: "actual_expense_attachments",
    params: 1,
    where: `expense_id IN (SELECT id FROM actual_expenses WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "actual_expenses", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "mileage_entries", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "acquisition_cost_lines", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "purchase_allocations", params: 1, where: `project_id = ${P}` },
  {
    kind: "special",
    id: "doomed-purchases",
    tables: ["purchase_attachments", "purchase_lines", "purchases"],
    why:
      "A purchase is SHARED: it is deleted only when every one of its allocations belonged to this project; otherwise it survives with this project's allocations removed.",
  },

  // ---- Planning / scope (Phases 4, 6, 8, 9B) ------------------------
  // Selections and procurement dispositions hang off requirement
  // components, and components reference the option that conditions them,
  // so: selections + dispositions -> components -> options -> instances.
  // Phase 12: the sale/closeout record is one row per project.
  { kind: "delete", table: "project_sales", params: 1, where: `project_id = ${P}` },
  // Phase 11: baselines and change orders are project history — deleted
  // with the project, and never before it. Change-order lines reference
  // their order; both reference the baseline they were approved against.
  {
    kind: "delete",
    table: "change_order_lines",
    params: 1,
    where: `change_order_id IN (SELECT id FROM change_orders WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "change_orders", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "change_order_sequences", params: 1, where: `project_id = ${P}`,
    why: "Change-order numbers are per project; the sequence dies with the project it numbered." },
  {
    kind: "delete",
    table: "baseline_lines",
    params: 1,
    where: `baseline_id IN (SELECT id FROM baselines WHERE project_id = ${P})`,
  },
  { kind: "delete", table: "baselines", params: 1, where: `project_id = ${P}` },
  // Phase 9E: an evidence decision records what was decided for one
  // component, so it goes before components (and before projects).
  { kind: "delete", table: "estimate_evidence_decisions", params: 1, where: `project_id = ${P}` },
  // Spec §90-37: the decision NOT to learn from this project's finished work.
  // The money is untouched; the exclusion goes with the project it names.
  { kind: "delete", table: "estimating_history_exclusions", params: 1, where: `project_id = ${P}` },
  /*
   * Spec §91-10: the decision not to learn from a finished work item. It
   * names a feature of THIS project, so it goes before the features and
   * with the project. Deleting it is not destroying evidence — the
   * accounting it points at is deleted by this same plan anyway.
   */
  { kind: "delete", table: "estimating_observation_exclusions", params: 1, where: `project_id = ${P}` },
  // Phase 10: package APPLICATIONS are project provenance (the library
  // packages themselves are shared and survive). The features and plan
  // objects that point at an application are deleted later in this plan,
  // so the pointers are severed first.
  {
    kind: "sql",
    sql: `UPDATE project_features SET package_application_id = NULL WHERE project_id = ${P}`,
    params: 1,
    why: "project_features.package_application_id references a row deleted below it in this plan.",
  },
  {
    kind: "sql",
    sql: `UPDATE plan_objects SET package_application_id = NULL WHERE design_id IN ${DESIGNS_OF_PROJECT}`,
    params: 1,
    why: "plan_objects.package_application_id references a row deleted below it in this plan.",
  },
  { kind: "delete", table: "package_applications", params: 1, where: `project_id = ${P}` },
  {
    kind: "delete",
    table: "project_product_selections",
    params: 1,
    where: `component_id IN ${COMPONENTS_OF_PROJECT}`,
  },
  { kind: "delete", table: "proposed_cost_lines", params: 1, where: `project_id = ${P}` },
  {
    kind: "delete",
    table: "procurement_dispositions",
    params: 1,
    where: `component_id IN ${COMPONENTS_OF_PROJECT}`,
  },
  {
    kind: "delete",
    table: "assembly_instance_components",
    params: 1,
    where: `instance_id IN ${INSTANCES_OF_PROJECT}`,
    why: "Components reference condition_option_id, so they precede options.",
  },
  {
    kind: "delete",
    table: "assembly_instance_options",
    params: 1,
    where: `instance_id IN ${INSTANCES_OF_PROJECT}`,
  },
  {
    kind: "delete",
    table: "assembly_instances",
    params: 1,
    where: `project_feature_id IN ${FEATURES_OF_PROJECT}`,
  },

  // ---- The schedule (spec §62) --------------------------------------
  // Dependencies reference two tasks, so they go before the tasks do.
  {
    kind: "delete",
    table: "task_dependencies",
    params: 1,
    where: `task_id IN (SELECT id FROM project_tasks WHERE project_id = ?)`,
  },
  { kind: "delete", table: "project_tasks", params: 1, where: "project_id = ?" },

  // ---- Design scenarios & geometry (Phases 7-9) ---------------------
  { kind: "delete", table: "change_scope_bindings", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  { kind: "delete", table: "design_change_dispositions", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  // Spec §60H: the differences a plan has not taken from Existing. Derived
  // from the designs themselves, so deleting the project deletes them with it.
  { kind: "delete", table: "design_upstream_changes", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  // Spec §61C: what the user decided about a clash in one of this project's
  // plans. The conflicts themselves are derived and stored nowhere.
  { kind: "delete", table: "design_conflict_dispositions", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  // Spec §63: a countertop's material and cutouts belong to the object that
  // carries them, and the cabinets it was measured from are a link between
  // two objects — both go before the objects do.
  {
    kind: "delete",
    table: "plan_countertop_cabinets",
    params: 1,
    where: `countertop_id IN (SELECT id FROM plan_objects WHERE design_id IN ${DESIGNS_OF_PROJECT})`,
  },
  {
    kind: "delete",
    table: "plan_countertops",
    params: 1,
    where: `object_id IN (SELECT id FROM plan_objects WHERE design_id IN ${DESIGNS_OF_PROJECT})`,
  },
  // Spec §60I: a staircase's dimensions belong to the object that carries
  // them, so they go before it.
  {
    kind: "delete",
    table: "plan_stairs",
    params: 1,
    where: `object_id IN (SELECT id FROM plan_objects WHERE design_id IN ${DESIGNS_OF_PROJECT})`,
  },
  { kind: "delete", table: "plan_objects", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  {
    kind: "delete",
    table: "wall_openings",
    params: 1,
    where: `wall_id IN (SELECT id FROM walls WHERE design_id IN ${DESIGNS_OF_PROJECT})`,
  },
  { kind: "delete", table: "walls", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  { kind: "delete", table: "plan_nodes", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },
  { kind: "delete", table: "design_room_geometry", params: 1, where: `design_id IN ${DESIGNS_OF_PROJECT}` },

  // ---- Structure (Phase 3) ------------------------------------------
  { kind: "delete", table: "project_features", params: 1, where: `project_id = ${P}` },
  {
    kind: "delete",
    table: "room_geometry",
    params: 1,
    where: `floor_id IN ${FLOORS_OF_PROJECT}`,
    why:
      "Superseded by design_room_geometry in v9, but pre-v9 databases still hold rows that reference rooms and floors.",
  },
  { kind: "delete", table: "rooms", params: 1, where: `floor_id IN ${FLOORS_OF_PROJECT}` },
  { kind: "delete", table: "floors", params: 1, where: `project_id = ${P}` },
  {
    kind: "sql",
    sql: `UPDATE projects SET active_design_id = NULL, approved_design_id = NULL WHERE id = ${P}`,
    params: 1,
    why: "projects.active_design_id and approved_design_id reference designs, so both pointers are severed before the designs go.",
  },
  { kind: "delete", table: "designs", params: 1, where: `project_id = ${P}` },
  // Saved report files: the association goes with the project; the file
  // itself survives if anything else still references it (below).
  { kind: "delete", table: "project_attachments", params: 1, where: `project_id = ${P}` },
  { kind: "delete", table: "projects", params: 1, where: `id = ${P}` },
  // NOTE: job_number_sequences is deliberately absent — job numbers are
  // never reclaimed (see NON_PROJECT_TABLES).

  {
    kind: "special",
    id: "orphaned-attachments",
    tables: ["attachments"],
    why:
      "Attachment records are SHA-256 deduplicated and shared; one is removed only when no association anywhere still references it, and its bytes only after the transaction commits.",
  },
];

/**
 * Tables that are deliberately NOT project data. Every entry is a decision
 * with a reason — shared library/catalog data must never quietly become
 * project-owned, and deleting a project must never delete it.
 */
export const NON_PROJECT_TABLES: Record<string, string> = {
  accounting_accounts:"Private business account registry; project sharing grants no book access.",
  accounting_setup:"Explicit business accounting cutover policy; no project owns it.",
  accounting_journals:"Immutable private business journals survive project deletion.",
  accounting_journal_lines:"Frozen business journal dimensions and names survive project deletion.",
  accounting_journal_seals:"Immutable balanced-posting evidence, business owned.",
  accounting_source_claims:"Canonical business posting identity, never project Actual.",
  accounting_source_aliases:"Private exactly-once source normalization metadata.",
  accounting_journal_reversals:"Immutable business reversal history.",
  accounting_requests:"Business posting retry identities retain their original result.",
  bank_reconciliation_statements:"Private business statement periods, not project budget reconciliation.",
  bank_statement_imports:"Immutable private bank statement evidence; never project shared.",
  bank_statement_rows:"Private imported statement facts and duplicates, business owned.",
  bank_statement_row_reviews:"Audited bank evidence review survives project deletion.",
  bank_reconciliation_matches:"Statement-to-existing-journal matching; never a second expense.",
  bank_reconciliation_events:"Private period close/reopen and match audit history.",
  schema_migrations: "Schema bookkeeping.",
  capital_sources:
    "Business-level capital (cash, owner funds, LOC) belongs to the whole operation, not any one project.",
  scenario_projects:
    "Planning fixtures for projects the business does NOT own yet; source_project_id is a plain severed reference and a scenario outlives the project it copied.",
  plan_settings: "Workspace-wide portfolio planning settings (reserve, horizon, objective, conservative assumptions).",
  company_funding_scenarios: "Private workspace planning assumptions only; no paid cost, debt, journal, or project ownership. Source references never authorize funding.",
  company_funding_runs: "Immutable private planning comparison provenance, not accounting or cash authority; survives project deletion.",
  plan_overrides:
    "Planning start overrides keyed by plain severed ids; a project deletion leaves at most a harmless stale row, never touches project truth.",
  app_settings: "Organization-level settings (labor rate, defaults).",
  project_statuses: "Shared status library.",
  company_classification_events: "Append-only company source classification review evidence; survives project deletion.",
  company_expense_categories: "Company classification, never project owned.",
  company_purchase_details: "Shared company source identity survives project deletion.",
  company_purchase_portions: "Company overhead portions refer to shared purchase lines, never project owned.",
  company_assets: "Company asset evidence survives project deletion.",
  company_commitment_matches: "Business-only bill/source matching metadata. Service validation requires a null-project commitment and an explicit company purchase or overhead portion, so no project deletion owns or removes these links.",
  expense_categories: "Shared category library.",
  room_types: "Shared room-type library.",
  room_type_feature_suggestions: "Shared library suggestions.",
  room_type_object_suggestions: "Shared library suggestions.",
  feature_definitions: "Shared feature-definition library.",
  object_definitions: "Shared design-object library.",
  object_definition_feature_suggestions: "Shared library suggestions.",
  assembly_templates: "Shared assembly templates (copy-on-use into projects).",
  assembly_template_components: "Shared assembly templates.",
  assembly_template_options: "Shared assembly templates.",
  assembly_suggestions: "Shared library suggestions.",
  material_definitions: "Shared material catalog.",
  material_preferred_products: "Shared material catalog.",
  products: "Shared product catalog; a product outlives any one project.",
  product_attributes: "Shared product catalog.",
  product_attachments: "Catalog attachments (reference-counted with the rest).",
  price_observations: "Append-only market/offer history; evidence outlives projects.",
  suppliers: "Shared supplier list.",
  provider_response_cache: "Provider response cache (quota control), not project data.",
  mileage_rates: "Organization mileage rates.",
  job_number_sequences: "Job numbers are NEVER reclaimed — the sequence survives deletion.",
  scope_packages: "Shared package library (org-owned); a package outlives any project that used it.",
  estimating_profiles: "Workspace-level estimating assumptions; a profile outlives every project that used it.",
  calibration_references:
    "Calibration knowledge learned FROM a project; project_id is a plain severed pointer and the reference deliberately survives the project's deletion.",
  calibration_reference_lines: "The calibration reference budgets, line by line (workspace knowledge).",
  profile_rates: "Calibrated estimating rates with provenance; workspace knowledge, not project data.",
  deals:
    "Opportunities evaluated BEFORE a project exists (spec §87); converted_project_id is a plain severed id and a deal's underwriting deliberately outlives the project it became.",
  deal_capital_sources: "The capital a deal was underwritten with (workspace-level, cascades with its deal).",
  deal_underwriting_snapshots:
    "The frozen underwriting taken at conversion; project_id is a plain id and the history survives the project's deletion.",
  scope_package_items: "Shared package library.",
  renovation_standards:
    "How the company normally renovates (spec §90) — workspace knowledge that outlives every project created from it; a project keeps its own frozen snapshot.",
  renovation_standard_finishes: "A renovation standard's finish choices (workspace knowledge, cascading with their standard).",
  renovation_standard_specs: "A renovation standard's specifications (workspace knowledge, cascading with their standard).",
  renovation_standard_room_packages: "A renovation standard's room packages (workspace knowledge, cascading with their standard).",
  scope_package_room_types: "Shared package library suggestions.",
};

/**
 * References the plan deliberately SEVERS instead of ordering around,
 * because the row holding them outlives (or is destroyed alongside) the row
 * they point at. Each is nulled by a `sql` step or by the doomed-purchase
 * stage before its target goes, and the coverage test skips exactly these
 * foreign keys — nothing else.
 */
export const SEVERED_REFERENCES: { table: string; column: string; why: string }[] = [
  {table:"draw_requests",column:"amendment_of",why:"Explicitly severed before deleting this project's request history."},
  {
    table: "projects",
    column: "approved_design_id",
    why: "The project points at the design its baseline approved; the pointer is nulled before the designs are deleted (a baseline stays readable history).",
  },
  {
    table: "projects",
    column: "active_design_id",
    why: "The project points at one of its own designs; the pointer is nulled before the designs are deleted.",
  },
  // projects.estimating_profile_id and calibration_references.project_id are
  // PLAIN ids by design (no FK): a shared profile needs no cleanup when a
  // project goes, and a calibration reference outlives its source project —
  // the coverage test only audits real foreign keys, so neither is listed.
  {
    table: "purchases",
    column: "original_purchase_id",
    why: "A return points at the purchase it offsets; if that purchase is destroyed at the owner's request the historical link is severed rather than cascading.",
  },
  {
    table: "project_features",
    column: "package_application_id",
    why: "Planning provenance only: the pointer is nulled before the applications go, and scope is never locked to the package that created it.",
  },
  {
    table: "plan_objects",
    column: "package_application_id",
    why: "Planning provenance only: the pointer is nulled before the applications go, and the object is never owned by the package.",
  },
  {
    table: "purchase_lines",
    column: "original_line_id",
    why: "Return lines point at the original line, severed with the purchase for the same reason.",
  },
];

/** Tables the plan removes rows from, in deletion order. */
export function plannedTables(): string[] {
  const out: string[] = [];
  for (const step of PROJECT_DELETION_PLAN) {
    if (step.kind === "delete") out.push(step.table);
    else if (step.kind === "special") out.push(...step.tables);
  }
  return out;
}

/** Position of a table in the deletion order (-1 when not covered). */
export function deletionIndexOf(table: string): number {
  let index = 0;
  for (const step of PROJECT_DELETION_PLAN) {
    if (step.kind === "delete") {
      if (step.table === table) return index;
    } else if (step.kind === "special") {
      if (step.tables.includes(table)) return index;
    }
    index++;
  }
  return -1;
}

/** Entries whose rows must be gone after a deletion (the residue check). */
export function projectOwnedTables(): ProjectOwnedTable[] {
  return PROJECT_DELETION_PLAN.filter(
    (s): s is { kind: "delete" } & ProjectOwnedTable => s.kind === "delete",
  );
}

export function deleteStatement(entry: ProjectOwnedTable): string {
  return `DELETE FROM ${entry.table} WHERE ${entry.where}`;
}

export function countStatement(entry: ProjectOwnedTable): string {
  return `SELECT COUNT(*) AS n FROM ${entry.table} WHERE ${entry.where}`;
}

/** The project id repeated for each placeholder in the predicate. */
export function bind(projectId: string, params: number): string[] {
  return Array.from({ length: params }, () => projectId);
}
